import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createPaginationContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

import { cutoffs } from '../../constants';

import LoadMore from '../Tiles/LoadMore';
import PortfolioFolders from './PortfolioFolders';

import actions from '../../actions/FavoritesActions';
import get from 'lodash.get';

class FoldersRefetchWrapper extends React.Component {
    constructor(props) {
        super(props);

        this.handleScroll = this.handleScroll.bind(this);
        this.loadMoreItems = this.loadMoreItems.bind(this);

        this.ticking = false;
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        window.addEventListener('scroll', this.handleScroll);
        const totalResults = get(this.props, 'viewer.portfolios.totalResults');
        if (totalResults >= 0) {
            this.props.setTotalResults(totalResults);
        }
    }

    componentDidUpdate(prevProps) {
        const prevTotalResults = get(prevProps, 'viewer.portfolios.totalResults');
        const totalResults = get(this.props, 'viewer.portfolios.totalResults');
        if (prevTotalResults !== totalResults) {
            this.props.setTotalResults(totalResults);
        }
    }

    componentWillUnmount() {
        this.props.setPartialPageLoading(false);
        window.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll() {
        const positionY = window.scrollY || window.pageYOffset;
        if (this.props.reordering) {
            if (!this.ticking) {
                window.requestAnimationFrame(() => {
                    const threshold =
                        window.innerHeight + positionY >= document.body.offsetHeight * 0.5;
                    this.ticking = false;
                    if (threshold && this.props.relay.hasMore() && !this.props.relay.isLoading()) {
                        this.loadMoreItems();
                    }
                });
            }
            this.ticking = true;
        }
    }

    loadMoreItems() {
        if (this.props.relay.hasMore()) {
            this.props.setPartialPageLoading(true);
            this.props.relay.loadMore(cutoffs.ADDITIONAL_ITEMS, () => {
                this.props.setPartialPageLoading(false);
                this.props.setNumItems(this.props.first + cutoffs.ADDITIONAL_ITEMS);
            });
        }
    }

    render() {
        const { first, isLoadingPartialPage, selectedItems, viewer } = this.props;
        const totalResults = get(viewer, 'portfolios.totalResults');

        if (!totalResults) {
            return null;
        }

        return (
            <Fragment>
                <PortfolioFolders
                    onDrop={this.props.onDrop}
                    selectedItems={selectedItems}
                    portfoliosConnection={viewer.portfolios || null}
                    reordered={this.props.reordered}
                />
                {!this.props.reordering ? (
                    <LoadMore
                        itemsShown={first}
                        isLoading={isLoadingPartialPage}
                        onClick={this.loadMoreItems}
                        totalItems={totalResults}
                    />
                ) : null}
            </Fragment>
        );
    }
}

FoldersRefetchWrapper.propTypes = {
    first: PropTypes.number,
    reordering: PropTypes.bool,
    isLoadingPartialPage: PropTypes.bool,
    addNote: PropTypes.func,
    onDrop: PropTypes.func,
    portfolioNode: PropTypes.object,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    selectedItems: PropTypes.array,
    setNumItems: PropTypes.func,
    setPartialPageLoading: PropTypes.func,
    setTotalResults: PropTypes.func,
    sortBy: PropTypes.string,
    viewer: PropTypes.object,
};

const mapStateToProps = state => {
    return {
        isLoadingPartialPage: state.favorites.uiState.isLoadingPartialPage,
        totalResults: state.favorites.uiState.totalResults,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setPartialPageLoading: isLoadingPartialPage => {
            dispatch(actions.setPartialPageLoading({ isLoadingPartialPage }));
        },
        setTotalResults: totalResults => {
            dispatch(actions.setTotalResults({ totalResults }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(FoldersRefetchWrapper);
FoldersRefetchWrapper = createPaginationContainer(
    ReduxWrapped,
    {
        viewer: graphql`
            fragment FoldersRefetchWrapper_viewer on Viewer {
                portfolios(
                    first: $first
                    after: $after
                    portfolioTypes: $portfolioTypes
                    sortBy: $foldersSort
                    userIds: $userIds
                ) @connection(key: "FoldersRefetchWrapper_portfolios") @include(if: $hasUserId) {
                    totalResults
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    edges {
                        cursor
                    }
                    ...PortfolioFolders_portfoliosConnection
                }
            }
        `,
    },
    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return props.viewer && props.viewer.portfolios;
        },
        getFragmentVariables(prevVars, first) {
            return {
                ...prevVars,
                first,
            };
        },
        getVariables(props, { count, cursor }, fragmentVariables) {
            return {
                first: count,
                after: cursor,
                foldersSort: props.sortBy,
                portfolioTypes: fragmentVariables.portfolioTypes,
                userIds: fragmentVariables.userIds,
                hasUserId: fragmentVariables.hasUserId,
            };
        },
        query: graphql`
            query FoldersRefetchWrapperRefetchQuery(
                $after: String
                $first: Int!
                $foldersSort: PortfoliosSortByTypes
                $portfolioTypes: [PortfolioTypes]
                $userIds: [String]!
                $hasUserId: Boolean!
            ) {
                viewer {
                    ...FoldersRefetchWrapper_viewer
                }
            }
        `,
    }
);

export default FoldersRefetchWrapper;
